import React, {useState} from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography
} from '@mui/material';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

const ScheduleFeedModal = ({ open, onClose, feedItem, onSchedule }) => {
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');

    if (!selectedDateTime) {
      setError('Please select a date and time');
      return;
    }

    // Validate that the date is in the future
    if (selectedDateTime.isBefore(moment())) {
      setError('Please select a future date and time');
      return;
    }

    setLoading(true);

    try {
      const scheduleData = {
        companyId: feedItem.companyId,
        year: selectedDateTime.year(),
        dayOfYear: selectedDateTime.dayOfYear(),
        hourOfDay: selectedDateTime.hour(),
        feedEntityId: feedItem.id
      };

      await onSchedule(scheduleData);
      handleClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to schedule feed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedDateTime(null);
    setError('');
    onClose();
  };

  return (
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            Schedule Feed for Later
          </DialogTitle>

          <DialogContent>
            <Box sx={{ pt: 2 }}>
              {/* Feed Information */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Feed Details
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> {feedItem?.type}
                </Typography>
                <Typography variant="body2">
                  <strong>Company:</strong> {feedItem?.companyName}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {feedItem?.status}
                </Typography>
              </Box>

              {/* Error Alert */}
              {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
              )}

              {/* Date Time Picker */}
              <Box sx={{ mb: 3 }}>
                <DateTimePicker
                    label="Schedule Date & Time"
                    value={selectedDateTime}
                    onChange={(newValue) => {
                      // Set minutes to 0 to enforce full hour selection
                      if (newValue) {
                        setSelectedDateTime(newValue.clone().minute(0).second(0));
                      } else {
                        setSelectedDateTime(null);
                      }
                    }}
                    renderInput={(params) => (
                        <TextField {...params} fullWidth />
                    )}
                    minDateTime={moment()}
                    ampm={false}
                    views={['year', 'day', 'hours']}
                    minutesStep={60}
                />
              </Box>

              {/* Schedule Info */}
              {selectedDateTime && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="primary.main">
                      <strong>Scheduled for:</strong> {selectedDateTime.format('MMMM Do YYYY, h:mm A')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      The feed will be automatically posted at the scheduled time
                    </Typography>
                  </Box>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
                onClick={handleSubmit}
                variant="contained"
                disabled={loading}
            >
              {loading ? 'Scheduling...' : 'Schedule Feed'}
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
  );
};

export default ScheduleFeedModal;
